import { FC, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
  type AITaskSetting,
  useBulkUpdateAITaskSettingsMutation,
  useListAITaskSettingsQuery,
  useUpdateAITaskSettingsMutation,
} from '@frontend/api-call-intel';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Accordion, Button, ContentLoader, Heading, useAlert } from '@frontend/design-system';
import { TASK_TYPE_LIST } from '../../constants';
import { useHasCallIntelTaskSettingAccess } from '../../hooks/use-has-call-intel-task-setting-access';
import { CallIntelSettingsTrackingIds } from '../../tracking-ids';
import { MultiLocationAccordion, SingleLocationAccordionItem } from '../location-settings-accordion';
import { UnauthorizedAccess } from '../unauthorized-access';
import { CreateTaskSettingsForm } from './create-task-settings-form';

export const CallIntelCreateTaskSettings: FC = () => {
  const { t } = useTranslation('settings', { keyPrefix: 'call-intel-settings' });
  const { selectedLocationIds } = useAppScopeStore();
  const [isManageAllView, setIsManageAllView] = useState(false);
  const {
    activeLocationIds,
    isAuthorizedOnAllLocations,
    authorizedLocationIds,
    authorizedLocationMap,
    isLoading: isLoadingAccess,
  } = useHasCallIntelTaskSettingAccess(selectedLocationIds);

  const { data: locationAiTaskSettingsMap, isLoading: isLoadingAITaskSettings } = useListAITaskSettingsQuery({
    request: {
      locationIds: authorizedLocationIds,
    },
    options: {
      enabled: authorizedLocationIds.length > 0,
      select: (data) => {
        return data.results?.reduce((acc, setting) => {
          acc[setting.locationId] = setting;
          return acc;
        }, {} as Record<string, AITaskSetting>);
      },
    },
  });

  if (isLoadingAccess || isLoadingAITaskSettings) {
    return (
      <div css={[defaultStyles.loadingCard, defaultStyles.container]}>
        <ContentLoader show />
      </div>
    );
  }

  // No eligible locations
  if (!activeLocationIds.length) {
    return (
      <div css={defaultStyles.container}>
        <UnauthorizedAccess
          containerStyles={defaultStyles.singleLocationCard}
          title={t('No eligible locations found.')}
        />
      </div>
    );
  }

  // Single location selected and active
  if (selectedLocationIds.length === 1) {
    const singleLocationId = selectedLocationIds[0];
    return (
      <div css={[defaultStyles.container, defaultStyles.singleLocationCard]}>
        <SingleLocationTaskSettings
          locationId={singleLocationId}
          aiTaskSettings={locationAiTaskSettingsMap?.[singleLocationId]}
          isAuthorized={authorizedLocationMap[singleLocationId]}
        />
      </div>
    );
  }

  // Multiple locations selected - show manage all / manage individual toggle
  return (
    <div css={defaultStyles.container}>
      <div css={defaultStyles.header}>
        <Heading level={3}>{t('Task Settings')}</Heading>
        <Button
          variant='tertiary'
          trackingId={CallIntelSettingsTrackingIds.createTaskSettings.manageAllButton}
          onClick={() => setIsManageAllView(!isManageAllView)}
          disabled={!isAuthorizedOnAllLocations}
        >
          {isManageAllView ? t('Manage Individual') : t('Manage All')}
        </Button>
      </div>
      {isManageAllView ? (
        <MultiLocationView locationIds={authorizedLocationIds} locationAiTaskSettingsMap={locationAiTaskSettingsMap} />
      ) : (
        <Accordion
          variant='location'
          showBoxShadow
          chevronSize={16}
          size='large'
          startingValue={activeLocationIds?.[0]}
        >
          {activeLocationIds?.map((locationId) => (
            <SingleLocationAccordionItem key={locationId} locationId={locationId}>
              <SingleLocationTaskSettings
                locationId={locationId}
                aiTaskSettings={locationAiTaskSettingsMap?.[locationId]}
                isAuthorized={authorizedLocationMap[locationId]}
              />
            </SingleLocationAccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
};

const SingleLocationTaskSettings: FC<{
  locationId: string;
  aiTaskSettings?: AITaskSetting;
  isAuthorized: boolean;
}> = ({ isAuthorized, aiTaskSettings, locationId }) => {
  const alerts = useAlert();
  const { t } = useTranslation('settings', { keyPrefix: 'call-intel-settings' });

  const { mutate: updateTaskSettings, isLoading: isUpdating } = useUpdateAITaskSettingsMutation({
    options: {
      onError: (error) => {
        alerts.error(t('Failed to update settings.'));
        sentry.warn({
          error: `Error updating Task settings: ${error}`,
          topic: 'analytics',
          team: 'analytics',
          addContext: {
            name: 'call-intel-pms-sync-settings',
            context: { error, locationId },
          },
        });
      },
    },
  });

  const handleSettingsToggle = (updatedValues?: Pick<AITaskSetting, 'aiTaskCategories' | 'aiTaskCreationEnabled'>) => {
    updateTaskSettings({
      aiTaskSettings: {
        locationId,
        ...updatedValues,
      },
    });
  };

  return (
    <CreateTaskSettingsForm
      isAuthorized={isAuthorized}
      values={aiTaskSettings}
      onToggle={handleSettingsToggle}
      isLoading={isUpdating}
    />
  );
};

const MultiLocationView: FC<{
  locationIds: string[];
  locationAiTaskSettingsMap?: Record<string, AITaskSetting>;
}> = ({ locationIds, locationAiTaskSettingsMap = {} }) => {
  const alerts = useAlert();
  const { t } = useTranslation('settings', { keyPrefix: 'call-intel-settings' });

  const mixedValues = useMemo<Pick<AITaskSetting, 'aiTaskCategories' | 'aiTaskCreationEnabled'>>(() => {
    const firstLocationSettings = locationAiTaskSettingsMap[locationIds[0]];

    return {
      aiTaskCreationEnabled: locationIds.some((locationId) => {
        const settings = locationAiTaskSettingsMap[locationId];
        return settings?.aiTaskCreationEnabled !== firstLocationSettings?.aiTaskCreationEnabled;
      }),
      aiTaskCategories: TASK_TYPE_LIST.reduce((acc, taskType) => {
        const firstLocationValue = firstLocationSettings.aiTaskCategories?.[taskType] || false;
        const isIndeterminate = locationIds.some((locationId) => {
          const settings = locationAiTaskSettingsMap[locationId];
          return (settings.aiTaskCategories?.[taskType] || false) !== firstLocationValue;
        });
        if (isIndeterminate) {
          acc[taskType] = true;
        }
        return acc;
      }, {} as Record<string, boolean>),
    };
  }, [locationIds, locationAiTaskSettingsMap]);

  const { mutate: bulkUpdateTaskSettings, isLoading: isBulkUpdating } = useBulkUpdateAITaskSettingsMutation({
    options: {
      onError: (error) => {
        alerts.error(t('Failed to update settings.'));
        sentry.warn({
          error: `Error updating bulk Task settings: ${error}`,
          topic: 'analytics',
          team: 'analytics',
          addContext: {
            name: 'call-intel-pms-sync-settings',
            context: { error, locationIds },
          },
        });
      },
    },
  });

  const handleSettingsToggle = (updatedValues?: Pick<AITaskSetting, 'aiTaskCategories' | 'aiTaskCreationEnabled'>) => {
    bulkUpdateTaskSettings({
      taskSettings: locationIds.map((locationId) => ({
        locationId,
        ...updatedValues,
      })),
    });
  };

  return (
    <MultiLocationAccordion locationIds={locationIds}>
      <CreateTaskSettingsForm
        isAuthorized
        values={locationAiTaskSettingsMap[locationIds[0]]}
        mixedValues={mixedValues}
        isLoading={isBulkUpdating}
        onToggle={handleSettingsToggle}
      />
    </MultiLocationAccordion>
  );
};

const defaultStyles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    max-width: 700px;
  `,
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  singleLocationCard: css`
    background-color: ${theme.colors.white};
    box-shadow: ${theme.shadows.light};
    border-radius: ${theme.borderRadius.medium};
    overflow: hidden;
  `,
  loadingCard: css`
    position: relative;
    background-color: ${theme.colors.white};
    box-shadow: ${theme.shadows.light};
    border-radius: ${theme.borderRadius.medium};
    height: 150px;
    width: 100%;
    overflow: hidden;
  `,
};
